import { useState } from 'react';
import { Modal, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import React from "react";

interface UseExcelUploadCheckOptions {
    /** 后端接口方法 */
    request: (params: { file: File }) => Promise<any>;
    /** 成功回调 */
    onSuccess?: () => void;
    /** 最大文件大小（MB），默认 1 */
    maxSizeMB?: number;
    /** 成功提示文案 */
    successMessage?: string;
}

export function useExcelUploadCheck(options: UseExcelUploadCheckOptions) {
    const {
        request,
        onSuccess,
        maxSizeMB = 1,
    } = options;

    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [loading, setLoading] = useState(false);

    /** 校验 Excel 文件 */
    const validateExcelFile = (file: File) => {
        const validTypes = [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ];

        const isValidType =
            validTypes.includes(file.type) ||
            file.name.endsWith('.xls') ||
            file.name.endsWith('.xlsx');

        if (!isValidType) {
            message.error('只能上传 Excel 文件（.xls 或 .xlsx）');
            return false;
        }

        if (file.size / 1024 / 1024 > maxSizeMB) {
            message.error(`文件大小不能超过 ${maxSizeMB}MB`);
            return false;
        }

        return true;
    };

    /** Upload 的 customRequest */
    const customRequest = ({ file, onSuccess }: any) => {
        if (!validateExcelFile(file)) return;

        const uploadFile: UploadFile = {
            uid: file.uid,
            name: file.name,
            status: 'done',
            originFileObj: file,
        };

        setFileList([uploadFile]);
        message.success('文件上传成功');
        onSuccess?.('ok');
    };

    /** 开始核验 */
    const startCheck = () => {
        try{
            if (loading) return;
            setLoading(true);
            const uploadFile = fileList[0];

            if (!uploadFile?.originFileObj) {
                message.warning('请先上传 Excel 文件');
                return;
            }
            let submitting = false;
            const realFile = uploadFile.originFileObj as File;
            Modal.confirm({
                title: '确认要开始核验吗？',
                icon: <ExclamationCircleOutlined />,
                content: (
                    <div>
                        <div>
                            即将核验的文件：<strong>{realFile.name}</strong>
                        </div>
                        <div style={{ marginTop: 8, color: '#faad14' }}>
                            任务一旦启动将不支持取消，请确认！
                        </div>
                    </div>
                ),
                okButtonProps: {
                    loading,
                    disabled: loading,
                },
                onOk: async () => {
                    try {
                        if (submitting) return;
                        submitting = true;

                        await request({ file: realFile });
                        setFileList([]);
                        onSuccess?.();
                    } catch (e) {
                        console.error('提交失败:', e);
                    } finally {
                        setTimeout(() => {setLoading(false);submitting=false}, 1000);
                    }
                },
                onCancel: () => {
                    submitting = false;
                    setLoading(false);
                },
            });
        }catch(e){
            console.error('表单验证失败:', e);
            setTimeout(() => setLoading(false), 1000);
        }
    };

    return {
        fileList,
        loading,
        customRequest,
        startCheck,
        removeFile: () => setFileList([]),
        finalDisabled: fileList.length > 0 && !loading,
    };
}
